// Zombie Solitaire — Klondike engine (pure JS, no React).
// State shape:
//   stock:        Card[]    (face-down draw pile)
//   waste:        Card[]    (face-up draw pile, top is last)
//   foundations:  Card[][]  (4 piles indexed by suit-order: 0..3)
//   tableau:      Card[][]  (7 columns; bottom of array = visually bottom-most card)
//   drawMode:     1 | 3
//   moves:        number
//   undoStack:    State[]   (snapshots before each move, capped at 50)
// Card: { id: string, suit: 'H'|'D'|'S'|'C', rank: 1..13, faceUp: boolean }

export const SUITS = ["H", "D", "S", "C"];
export const RANKS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13];

const RED = new Set(["H", "D"]);
export const isRed = (suit) => RED.has(suit);

function makeDeck() {
  const out = [];
  for (const s of SUITS) {
    for (const r of RANKS) {
      out.push({ id: `${s}${r}`, suit: s, rank: r, faceUp: false });
    }
  }
  return out;
}

function shuffle(arr, rng = Math.random) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export function newGame(drawMode = 1, seed = null) {
  const rng = seed == null ? Math.random : mulberry32(seed);
  const deck = shuffle(makeDeck(), rng);
  const tableau = [[], [], [], [], [], [], []];
  let idx = 0;
  for (let col = 0; col < 7; col++) {
    for (let row = 0; row <= col; row++) {
      const c = { ...deck[idx++], faceUp: row === col };
      tableau[col].push(c);
    }
  }
  const stock = deck.slice(idx).map((c) => ({ ...c, faceUp: false }));
  return {
    stock,
    waste: [],
    foundations: [[], [], [], []],
    tableau,
    drawMode: drawMode === 3 ? 3 : 1,
    moves: 0,
    undoStack: [],
    startTime: Date.now(),
  };
}

function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function clone(state) {
  return {
    stock: state.stock.map((c) => ({ ...c })),
    waste: state.waste.map((c) => ({ ...c })),
    foundations: state.foundations.map((p) => p.map((c) => ({ ...c }))),
    tableau: state.tableau.map((p) => p.map((c) => ({ ...c }))),
    drawMode: state.drawMode,
    moves: state.moves,
    undoStack: [],
    startTime: state.startTime,
  };
}

function pushUndo(state, snapshot) {
  const next = [...state.undoStack, snapshot];
  if (next.length > 50) next.shift();
  return next;
}

function flipBottomIfNeeded(col) {
  if (col.length && !col[col.length - 1].faceUp) {
    col[col.length - 1] = { ...col[col.length - 1], faceUp: true };
  }
}

export function suitForFoundationIdx(idx) {
  return SUITS[idx];
}

export function foundationIdxForSuit(suit) {
  return SUITS.indexOf(suit);
}

// Can a single card be placed on a foundation?
export function canPlaceOnFoundation(card, foundation) {
  if (!card) return false;
  const top = foundation[foundation.length - 1];
  if (!top) return card.rank === 1; // Ace starts
  return card.suit === top.suit && card.rank === top.rank + 1;
}

// Can a card (or a stack head) be placed onto a tableau column?
function canPlaceOnTableau(card, column) {
  if (!card || !card.faceUp) return false;
  const bottom = column[column.length - 1];
  if (!bottom) return card.rank === 13; // King on empty
  if (!bottom.faceUp) return false;
  return isRed(card.suit) !== isRed(bottom.suit) && card.rank === bottom.rank - 1;
}

// Validate that a tableau pile slice (from index `fromIdx` to end) forms
// a valid moveable sequence (alternating colors, descending ranks).
export function validTableauStack(cards) {
  for (let i = 0; i < cards.length; i++) {
    if (!cards[i].faceUp) return false;
    if (i > 0) {
      const prev = cards[i - 1];
      const cur = cards[i];
      if (isRed(prev.suit) === isRed(cur.suit)) return false;
      if (cur.rank !== prev.rank - 1) return false;
    }
  }
  return true;
}

// ----- Moves return either a new state OR null if invalid -----
export function drawFromStock(state) {
  const snap = clone(state);
  const next = clone(state);
  if (next.stock.length === 0) {
    // Recycle waste → stock (face-down, in reverse)
    if (next.waste.length === 0) return null;
    next.stock = next.waste.reverse().map((c) => ({ ...c, faceUp: false }));
    next.waste = [];
  } else {
    const n = Math.min(next.drawMode, next.stock.length);
    for (let i = 0; i < n; i++) {
      const c = next.stock.pop();
      next.waste.push({ ...c, faceUp: true });
    }
  }
  next.moves = state.moves + 1;
  next.undoStack = pushUndo(state, snap);
  return next;
}

// Move from one location to another. `from` and `to` are descriptors:
//   { type: 'waste' }
//   { type: 'tableau', col: 0..6, index?: number }  (index = top card of slice; omit for last)
//   { type: 'foundation', idx: 0..3 }
export function tryMove(state, from, to) {
  const snap = clone(state);
  const next = clone(state);
  const cards = extractCards(next, from);
  if (!cards || cards.length === 0) return null;
  const ok = placeCards(next, cards, to);
  if (!ok) return null;
  // After a successful tableau pull, flip the new bottom face-up card if needed
  if (from.type === "tableau") flipBottomIfNeeded(next.tableau[from.col]);
  next.moves = state.moves + 1;
  next.undoStack = pushUndo(state, snap);
  return next;
}

function extractCards(state, from) {
  if (from.type === "waste") {
    if (state.waste.length === 0) return null;
    const c = state.waste.pop();
    return [c];
  }
  if (from.type === "foundation") {
    const f = state.foundations[from.idx];
    if (!f.length) return null;
    return [f.pop()];
  }
  if (from.type === "tableau") {
    const col = state.tableau[from.col];
    const idx = from.index == null ? col.length - 1 : from.index;
    if (idx < 0 || idx >= col.length) return null;
    const slice = col.slice(idx);
    if (!validTableauStack(slice)) return null;
    state.tableau[from.col] = col.slice(0, idx);
    return slice;
  }
  return null;
}

function placeCards(state, cards, to) {
  if (to.type === "foundation") {
    if (cards.length !== 1) return false;
    const idx = to.idx;
    if (!canPlaceOnFoundation(cards[0], state.foundations[idx])) return false;
    state.foundations[idx].push(cards[0]);
    return true;
  }
  if (to.type === "tableau") {
    const col = state.tableau[to.col];
    if (!canPlaceOnTableau(cards[0], col)) return false;
    col.push(...cards);
    return true;
  }
  return false;
}

// Auto-move: find the best target for a card (or stack) and move it.
// Priority: foundation > tableau (where it builds a sequence) > nothing.
export function autoMove(state, from) {
  const probe = clone(state);
  const cards = extractCards(probe, { ...from });
  if (!cards) return null;
  // Restore probe and run real moves through tryMove to keep undo correct.

  // 1) Try foundation if single card
  if (cards.length === 1) {
    for (let i = 0; i < 4; i++) {
      if (canPlaceOnFoundation(cards[0], state.foundations[i])) {
        return tryMove(state, from, { type: "foundation", idx: i });
      }
    }
  }
  // 2) Try tableau columns (prefer non-empty, then empty for Kings)
  for (let c = 0; c < 7; c++) {
    if (from.type === "tableau" && c === from.col) continue;
    if (state.tableau[c].length === 0) continue;
    if (canPlaceOnTableau(cards[0], state.tableau[c])) {
      return tryMove(state, from, { type: "tableau", col: c });
    }
  }
  for (let c = 0; c < 7; c++) {
    if (from.type === "tableau" && c === from.col) continue;
    if (state.tableau[c].length !== 0) continue;
    if (canPlaceOnTableau(cards[0], state.tableau[c])) {
      return tryMove(state, from, { type: "tableau", col: c });
    }
  }
  return null;
}

// Find a hint: returns { from, to } or null.
export function findHint(state) {
  // 1) Any card that can go to foundation
  const sources = [];
  if (state.waste.length) sources.push({ type: "waste" });
  for (let c = 0; c < 7; c++) {
    const col = state.tableau[c];
    if (col.length && col[col.length - 1].faceUp) {
      sources.push({ type: "tableau", col: c, index: col.length - 1 });
    }
  }
  for (const src of sources) {
    const probe = clone(state);
    const cards = extractCards(probe, { ...src });
    if (!cards || cards.length !== 1) continue;
    for (let i = 0; i < 4; i++) {
      if (canPlaceOnFoundation(cards[0], state.foundations[i])) {
        return { from: src, to: { type: "foundation", idx: i } };
      }
    }
  }
  // 2) Any tableau-tableau move that uncovers a face-down card (high value)
  for (let c = 0; c < 7; c++) {
    const col = state.tableau[c];
    if (!col.length) continue;
    // find topmost face-up index
    let firstUp = col.findIndex((x) => x.faceUp);
    if (firstUp < 0) continue;
    if (firstUp === 0) continue; // moving exposes nothing
    const slice = col.slice(firstUp);
    if (!validTableauStack(slice)) continue;
    for (let d = 0; d < 7; d++) {
      if (d === c) continue;
      if (canPlaceOnTableau(slice[0], state.tableau[d])) {
        return { from: { type: "tableau", col: c, index: firstUp }, to: { type: "tableau", col: d } };
      }
    }
  }
  // 3) Any other valid tableau-tableau move
  for (let c = 0; c < 7; c++) {
    const col = state.tableau[c];
    if (!col.length) continue;
    const slice = col.slice(col.findIndex((x) => x.faceUp));
    if (slice.length === 0 || !validTableauStack(slice)) continue;
    for (let d = 0; d < 7; d++) {
      if (d === c) continue;
      if (canPlaceOnTableau(slice[0], state.tableau[d])) {
        return { from: { type: "tableau", col: c, index: col.length - slice.length }, to: { type: "tableau", col: d } };
      }
    }
  }
  // 4) Waste → tableau
  if (state.waste.length) {
    const top = state.waste[state.waste.length - 1];
    for (let d = 0; d < 7; d++) {
      if (canPlaceOnTableau(top, state.tableau[d])) {
        return { from: { type: "waste" }, to: { type: "tableau", col: d } };
      }
    }
  }
  // 5) Otherwise hint to draw
  if (state.stock.length || state.waste.length) {
    return { from: { type: "stock" }, to: { type: "stock" } };
  }
  return null;
}

export function isWin(state) {
  return state.foundations.every((f) => f.length === 13);
}

export function undo(state) {
  if (!state.undoStack.length) return null;
  const prev = state.undoStack[state.undoStack.length - 1];
  return { ...prev, undoStack: state.undoStack.slice(0, -1) };
}
